import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useSuccessFailure } from '../context/SuccessFailureContext';
import config from "../config";
import { Helmet } from 'react-helmet-async';

const GIGS_PER_PAGE = 12;

const MyGigs = () => {
    const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
    const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || 'We make events sawa';
    const { user: loggedInUser } = useAuth();
    const [hiredGigs, setHiredGigs] = useState([]);
    const [reviews, setReviews] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [visibleReviews, setVisibleReviews] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [reviewers, setReviewers] = useState({});
    const [completingBidId, setCompletingBidId] = useState(null);

    const { showSuccessModal, showFailureModal } = useSuccessFailure();

    useEffect(() => {
        if (!loggedInUser) return;

        const token = localStorage.getItem("access_token");

        const fetchData = async () => {
            try {
                const res = await axios.get(`${config.API_BASE_URL}/bids/hired/${loggedInUser.id}/`, {
                    headers: { Authorization: token ? `Bearer ${token}` : "" },
                });

                // console.log("Fetched hired gigs:", res.data); // Log the hired gigs

                setHiredGigs(res.data);
                setLoading(false);

                const reviewsData = {};
                const reviewersData = {};

                await Promise.all(
                    res.data.map(async (gig) => {
                        try {
                            const reviewRes = await axios.get(
                                `${config.API_BASE_URL}/bids/reviews/${gig.id}/`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );

                            const review = reviewRes.data;
                            // console.log("Fetched review for gig:", gig.id, review); // Log each fetched review

                            reviewsData[gig.id] = review;

                            const reviewerId = gig.assigned_to_id; // Fix here, use gig.assigned_to_id for reviewer ID
                            // console.log("Reviewer id  for gig:", reviewerId); // Log each fetched review

                            if (reviewerId && !reviewersData[reviewerId]) {
                                const profileRes = await axios.get(
                                    `${config.API_BASE_URL}/profiles/profile/user/${reviewerId}/`, // Fetching profile using reviewer_id
                                    { headers: { Authorization: `Bearer ${token}` } }
                                );

                                // console.log("Fetched profile for reviewer:", reviewerId, profileRes.data); // Log the reviewer profile

                                reviewersData[reviewerId] = profileRes.data?.slug || "Anonymous";
                            }
                        } catch (err) {
                            // console.error("Error fetching review/profile:", err);
                        }
                    })
                );

                setReviews(reviewsData);
                setReviewers(reviewersData);
            } catch (err) {
                // console.error("Error fetching gigs:", err);
                setError("Failed to load gigs.");
                setLoading(false);
            }
        };

        fetchData();
    }, [loggedInUser]);

    const handleMarkAsComplete = async (bidId) => {
        setCompletingBidId(bidId); // set the currently loading bid
        try {
            const token = localStorage.getItem("access_token");
            const response = await axios.put(
                `${config.API_BASE_URL}/bids/${bidId}/mark-complete/`,
                {},
                { headers: { Authorization: token ? `Bearer ${token}` : "" } }
            );

            if (response.status === 200) {
                showSuccessModal("Service request marked as completed!");

                setHiredGigs((prevGigs) =>
                    prevGigs.map((gig) =>
                        gig.id === bidId ? { ...gig, status: "pending_completion_confirmation" } : gig
                    )
                );
            }
        } catch (error) {
            // console.error("Error updating bid status:", error);
        } finally {
            setCompletingBidId(null); // reset the loading state
        }
    };

    const paginatedGigs = hiredGigs.slice(
        (currentPage - 1) * GIGS_PER_PAGE,
        currentPage * GIGS_PER_PAGE
    );

    const totalPages = Math.ceil(hiredGigs.length / GIGS_PER_PAGE);

    if (loading) return <p className="text-center mt-6">Loading...</p>;
    if (error) return <p className="text-center mt-6 text-red-500">{error}</p>;

    return (
        <div className="container mx-auto p-6">
            <Helmet>
                <title>{eventTitle}</title>
                <meta name="description" content={eventDescription} />
            </Helmet>
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                    My Gigs
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    A summary of all your ongoing, completed, and reviewed work.
                </p>
            </div>
            {paginatedGigs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    {paginatedGigs.map((gig) => (
                        <div key={gig.id} className="border p-4 rounded-lg shadow-md relative bg-white self-start">
                            <h3 className="text-lg font-bold mb-2">{gig.title}</h3>

                            {gig.status === "completed" || gig.status === "pending_completion_confirmation" ? (
                                <p className="font-semibold flex items-center gap-2">
                                    Status:
                                    {gig.status === "pending_completion_confirmation" ? (
                                        <span className="text-yellow-500 font-bold">Waiting for confirmation</span>
                                    ) : (
                                        <span className="text-green-600 font-bold">Completed</span>
                                    )}
                                </p>
                            ) : (
                                <button
                                    className={`px-4 py-1 rounded-lg text-sm mt-2 flex items-center justify-center ${completingBidId === gig.id ? "bg-blue-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                                        } text-white`}
                                    onClick={() => handleMarkAsComplete(gig.id)}
                                    disabled={completingBidId === gig.id}
                                >
                                    {completingBidId === gig.id ? (
                                        <svg className="animate-spin h-4 w-4 mr-2 text-white" viewBox="0 0 24 24">
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                            />
                                        </svg>
                                    ) : null}
                                    {completingBidId === gig.id ? "Completing..." : "Mark as Complete"}
                                </button>
                            )}

                            {reviews[gig.id] && Object.keys(reviews[gig.id]).length > 0 ? (
                                <>
                                    <button
                                        className="absolute top-2 right-2 px-3 py-1 bg-gray-800 text-white rounded-lg text-sm"
                                        onClick={() =>
                                            setVisibleReviews((prev) => ({
                                                ...prev,
                                                [gig.id]: !prev[gig.id],
                                            }))
                                        }
                                    >
                                        {visibleReviews[gig.id] ? "Hide Review" : "See Review"}
                                    </button>

                                    {visibleReviews[gig.id] && (
                                        <div className="mt-3 p-2 border-t border-gray-300">
                                            <div className="flex items-center justify-between mb-1 text-xs text-gray-500">
                                                <span>{reviewers[gig.assigned_to_id] || "Anonymous"}</span>
                                                <span>
                                                    {new Date(gig.created_at).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>

                                            <div className="flex items-center text-yellow-500 text-base mb-2">
                                                {[...Array(5)].map((_, index) => (
                                                    <span key={index} className={index < reviews[gig.id]?.rating ? "text-yellow-500" : "text-gray-300"}>
                                                        ★
                                                    </span>
                                                ))}
                                            </div>

                                            <p className="text-sm text-gray-700 italic">
                                                {reviews[gig.id]?.review_text || "No review text available"}
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-sm text-gray-700 mt-2">No reviews available</p>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <p>No gigs found.</p>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center mt-6 space-x-2">
                    {[...Array(totalPages)].map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-3 py-1 border rounded ${currentPage === index + 1
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                }`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MyGigs;